import { type } from 'arktype';

/**
 * Domain constants for the projects domain.
 *
 * Values mirror the pg enums in `libs/db/src/schema/projects.ts` —
 * keep both in sync.
 */

/** Fixed MVP project status set (see spec: project-time-tracking.md). */
export const PROJECT_STATUSES = ['draft', 'active', 'on_hold', 'completed'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

/** Role of a person assigned to a project. */
export const PROJECT_MEMBER_ROLES = ['member', 'responsible'] as const;
export type ProjectMemberRole = (typeof PROJECT_MEMBER_ROLES)[number];

export const projectStatusSchema = type("'draft'|'active'|'on_hold'|'completed'").configure({
  description: 'Project status. Only `active` leaf projects are bookable.',
  examples: ['active'],
});

export const projectMemberRoleSchema = type("'member'|'responsible'").configure({
  description: "Role of the assigned person: 'member' (Mitarbeiter) or 'responsible' (Verantwortlicher).",
  examples: ['member'],
});

/**
 * ISO calendar date (`YYYY-MM-DD`). The regex gates the shape; the narrow
 * gates the calendar (rejects `2026-13-45`, `2026-02-30`, …) via a UTC
 * roundtrip — same convention as the timetracking models — so impossible
 * dates fail with a 400 instead of a Postgres `date/time field value out of
 * range` error mapped to a 500.
 */
export const isoDateSchema = type(/^\d{4}-\d{2}-\d{2}$/)
  .narrow((date, ctx) => {
    const parsed = new Date(`${date}T00:00:00Z`);
    return (!Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date) || ctx.mustBe('a valid calendar date');
  })
  .configure({
    description: 'ISO calendar date (YYYY-MM-DD).',
    examples: ['2026-07-01'],
  });

/** `true` when the value is a real `YYYY-MM-DD` calendar date (shared with query-param parsing). */
export function isValidIsoDate(value: string): boolean {
  return !(isoDateSchema(value) instanceof type.errors);
}

/**
 * Project master data. Hierarchical via `parentId` — parent projects are pure
 * grouping nodes (only leaf projects are bookable).
 */
export interface Project {
  id: string;
  /** Human-readable unique project number, e.g. "P-2041". */
  number: string;
  name: string;
  /** Free-text customer name (MVP — no customer domain yet). */
  customer: string | null;
  status: ProjectStatus;
  /** Parent project id; `null` = root node. */
  parentId: string | null;
  startDate: string | null;
  endDate: string | null;
  /** Optional structured invoice recipient address (spec: faktura.md, Story 5) — additive to `customer`, both optional. */
  billingName: string | null;
  billingAddress: string | null;
  billingCity: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Person assigned to a project, with display data for avatars/initials. */
export interface ProjectMemberInfo {
  personId: string;
  role: ProjectMemberRole;
  firstName: string;
  lastName: string;
}

/** Activity type assigned to a project with its optional hour budget. */
export interface ProjectActivityInfo {
  activityTypeId: string;
  name: string;
  billable: boolean;
  /** Optional planned hours for this activity on this project. */
  budgetHours: number | null;
}

/**
 * Project list item with aggregates. For parent projects (grouping nodes)
 * `budgetHours` and `bookedMinutes` include all transitive children;
 * `members`/`memberCount` always reflect the project's own assignments.
 */
export interface ProjectListItem extends Project {
  /** Sum of assigned activity budgets in hours; `null` when no budget is set anywhere. */
  budgetHours: number | null;
  /** Sum of booked minutes from project time entries. */
  bookedMinutes: number;
  /** Sum of booked minutes flagged billable (subset of `bookedMinutes`). */
  billableBookedMinutes: number;
  members: ProjectMemberInfo[];
  memberCount: number;
  hasChildren: boolean;
}

/** Project detail: list aggregates plus the project's own activity assignments. */
export interface ProjectDetail extends ProjectListItem {
  activities: ProjectActivityInfo[];
}

const projectNumberSchema = type('string>=1 & string<=50').configure({
  description: 'Unique human-readable project number.',
  examples: ['P-2041'],
});

const projectNameSchema = type('string>=1 & string<=200').configure({
  description: 'Project name.',
  examples: ['Website Relaunch'],
});

const customerSchema = type('string<=200 | null').configure({
  description: 'Free-text customer name. `null` clears the field.',
  examples: ['Acme AG'],
});

const parentIdSchema = type('string.uuid | null').configure({
  description: 'Parent project id (grouping node). `null` makes the project a root.',
});

const optionalDateSchema = isoDateSchema.or('null');

/** Optional structured invoice recipient address fields (spec: faktura.md, Story 5) — all nullable, none required. */
const billingNameSchema = type('string<=200')
  .or('null')
  .configure({
    description: 'Invoice recipient company/person name. `null` clears the field.',
    examples: ['Acme AG'],
  });
const billingAddressSchema = type('string<=200')
  .or('null')
  .configure({
    description: 'Invoice recipient street address. `null` clears the field.',
    examples: ['Musterstrasse 1'],
  });
const billingCitySchema = type('string<=200')
  .or('null')
  .configure({
    description: 'Invoice recipient postal code + city. `null` clears the field.',
    examples: ['8000 Zürich'],
  });

export const createProjectSchema = type({
  number: projectNumberSchema,
  name: projectNameSchema,
  'customer?': customerSchema,
  'status?': projectStatusSchema,
  'parentId?': parentIdSchema,
  'startDate?': optionalDateSchema,
  'endDate?': optionalDateSchema,
  'billingName?': billingNameSchema,
  'billingAddress?': billingAddressSchema,
  'billingCity?': billingCitySchema,
}).configure({ description: 'Payload for creating a new project.' });
export type CreateProject = typeof createProjectSchema.t;

export const updateProjectSchema = type({
  'number?': projectNumberSchema,
  'name?': projectNameSchema,
  'customer?': customerSchema,
  'status?': projectStatusSchema,
  'parentId?': parentIdSchema,
  'startDate?': optionalDateSchema,
  'endDate?': optionalDateSchema,
  'billingName?': billingNameSchema,
  'billingAddress?': billingAddressSchema,
  'billingCity?': billingCitySchema,
}).configure({ description: 'Partial update for a project.' });
export type UpdateProject = typeof updateProjectSchema.t;

/**
 * Budget bounds mirror the `numeric(8, 2)` column: at most two decimal
 * places and a value the precision can hold — anything else would surface
 * as a Postgres `numeric field overflow` / check-constraint 500 instead of
 * a friendly 400.
 */
const budgetHoursValueSchema = type('0.01 <= number <= 999999').narrow(
  (value, ctx) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-9 || ctx.mustBe('a number with at most two decimal places')
);

const projectActivityAssignmentSchema = type({
  activityTypeId: type('string.uuid').configure({
    description: 'Id of the assigned activity type.',
  }),
  'budgetHours?': budgetHoursValueSchema.or('null').configure({
    description: 'Optional planned hours for this activity on this project (0.01–999999, max two decimals). `null` = no budget.',
    examples: [40],
  }),
});
export type ProjectActivityAssignment = typeof projectActivityAssignmentSchema.t;

export const replaceProjectActivitiesSchema = type({
  activities: projectActivityAssignmentSchema.array().configure({
    description: 'Full replacement set of activity assignments for the project.',
  }),
}).configure({ description: 'Replaces all activity assignments (incl. budgets) of a project.' });
export type ReplaceProjectActivities = typeof replaceProjectActivitiesSchema.t;

const projectMemberAssignmentSchema = type({
  personId: type('string.uuid').configure({
    description: 'Id of the assigned person (core.persons).',
  }),
  role: projectMemberRoleSchema,
});
export type ProjectMemberAssignment = typeof projectMemberAssignmentSchema.t;

export const replaceProjectMembersSchema = type({
  members: projectMemberAssignmentSchema.array().configure({
    description: 'Full replacement set of person assignments for the project.',
  }),
}).configure({ description: 'Replaces all person assignments (incl. roles) of a project.' });
export type ReplaceProjectMembers = typeof replaceProjectMembersSchema.t;
